/**
 * What the customer has said, and nothing else.
 *
 * vB6 keeps one mutable `S` and re-renders the whole page from it. The port
 * keeps the same shape, as one immutable value in one `useState`: every field
 * below is a field vB6 has, under the name vB6 gives it where the name still
 * says what it holds.
 *
 * Nothing derived lives here. The quantities, the money, the lane, the
 * blockers and the lists are worked out in `model.ts` from this value and one
 * reading of `public.menu_context()`, so there is no second copy of a figure
 * to go stale — which is the thing §9 of `docs/overnight_constraints.md` asks
 * every stage to look for.
 */

import { CONFERENCE_DEFAULTS, type Balance, type ConferenceSelection } from '@plt/engine'

import type { Bilingual, Lang } from './lang.ts'

export type Fulfilment = 'delivery' | 'pickup'
export type Filter = 'all' | 'sav' | 'sw' | 'gm' | 'ital'
export type PaymentMethod = 'card' | 'transfer'

export interface AddressFields {
  postcode: string
  town: string
  street: string
  floor: string
  /**
   * Specification §3.7: the customer typed the town, so a postcode lookup
   * never overwrites it (`towns.ts`). False for a town the lookup filled or
   * the customer picked from its list.
   */
  townTyped?: boolean
}

export const emptyAddress = (): AddressFields => ({
  postcode: '',
  town: '',
  street: '',
  floor: '',
  townTyped: false,
})

/**
 * The postcode the page opens on: Planteen's own district, which is where
 * most of its events are and the one vB6 opens on. It is a starting point the
 * customer types over, not a claim about where the food is going.
 */
export const DEFAULT_POSTCODE = '1033'

export interface Selection {
  sav: string[]
  sw: string[]
}

/** What was sent, recorded at the moment it was sent (DR-6, FIXES item 9). */
export interface SentRecord {
  ref: string
  enquiry: boolean
  /**
   * `build/FIXES.md` item 79 (F8): the order was sent for confirmation, so
   * nothing was paid and the next thing to happen is a colleague's answer.
   * Recorded at the moment of sending, like every other field here, so the
   * confirmation panel cannot be rewritten by a later render (DR-6).
   */
  confirmation: boolean
  phone: string
  email: string
  payment: PaymentMethod
  reviewed: boolean
  pickup: boolean
  quoted: boolean
  gross: number
  slot: string
  /** Instants, formatted at render time: a date format is a language matter. */
  eventAt: number | null
  handoverAt: number | null
  /** §9.4: the token the return page reads the order by. Null on an enquiry. */
  publicToken: string | null
  /** §9.1: what happens next, as `submit_order` answered it. */
  next: string | null
}

export interface CallbackRecord {
  phone: string
  /** The business day the request lapses on (§5.7, `callback_expiry_bd`). */
  expiresAt: number
}

export interface PageState {
  lang: Lang

  /* 01 · the event */
  eventName: string
  /** The raw field, so a half-typed number is not corrected under the hand. */
  guestsField: string
  /** DR-7 (FIXES item 10): the headcount was clamped and the line says why. */
  guestsCapped: boolean
  date: string
  start: string
  occasion: string
  servingWindow: string
  profile: string
  balance: Balance
  /**
   * Run 17 item B11(a): the customer answered the meal question themselves,
   * so a later change of start time leaves their answer where it is. Cleared
   * when the occasion changes, because the pre-selection is about the day the
   * new occasion stands for.
   */
  mealTouched: boolean
  staff: boolean
  planteenOnly: boolean
  /**
   * `build/FIXES.md` item 73 (F2): the three answers that make up an all-day
   * conference. They are read for the conference occasion and ignored for
   * every other one, so they are kept rather than cleared when the occasion
   * changes — a customer who looks at Kávészünet and comes back finds the day
   * they built. The engine's `CONFERENCE_DEFAULTS` is what they open on.
   */
  conference: ConferenceSelection
  /**
   * `build/FIXES.md` item 78 (F7): *Kér kávét is?*, default *Nem*. Switching
   * to another occasion clears it, which is the item's own rule.
   */
  coffee: boolean
  /** §5.2: gluten-free guests, as typed. Every reader clamps for itself. */
  glutenFreeGuests: number
  dietaryNote: string
  fulfilment: Fulfilment
  postcode: string
  slotId: string

  /* 02 · the menu */
  selection: Selection | null
  /** The occasion and lane the selection was made for (DR-21, FIXES item 24). */
  selectionKey: string
  touched: { sav: boolean; sw: boolean }
  /** DR-21: a page limit took dishes off a side just now. */
  clipped: boolean
  /** The limits that were in force when it did. */
  clipLimitKey: string
  /**
   * Run 15 item 1 (DECIDED 20261015, Barna), which replaces *Többet kérek
   * ebből*: the quantity the customer set for a dish with − / +, as dish ID →
   * portions. A dish not named here is on its recommendation; a dish named
   * here is made in exactly that quantity whatever else moves, and is stored
   * with `qty_source = 'customer'` (item 1(j)).
   *
   * The figure is the dish's **total** on its side, the gluten-free box share
   * included, because run 9 item 5 and run 14 item 3 both measure a dish
   * once, main and box together. The box itself stays Planteen's (item 1(g)).
   */
  quantities: Record<string, number>
  /**
   * Run 15 item 1(e): the headcount, the appetite or the balance moved while
   * a changed dish was on the menu, so the one sentence saying the changed
   * dishes held their quantities belongs under the menu. Cleared as soon as
   * no changed dish is left.
   */
  quantitiesHeld: boolean
  /**
   * Run 15 item 1(f): the dish − took off the menu, with the quantity it had,
   * so *Visszavonás* can put it back exactly as it was. A dish re-added any
   * other way comes back on its recommendation, which is why the quantity
   * lives here and not in `quantities`.
   */
  removed: { dishId: string; side: 'sav' | 'sw'; portions: number } | null
  /** DR-4 (FIXES item 7): the sections the customer has opened. */
  sections: string[]
  filter: Filter
  sub: string | null
  fruit: string[]
  /**
   * Run 15 item 2: a drink the customer adds is a **total quantity**. The
   * stepper counts bottles, its step is the drink's production unit, and a
   * headcount change never multiplies it. §5.6's count per guest is retired
   * for the drinks the customer ticks; the lines the build adds for itself
   * (F3's refreshment drink, F7's coffee) are untouched and stay per guest.
   */
  drinks: Record<string, { on: boolean; bottles: number }>
  custom: boolean

  /* 03 · contact and billing */
  business: boolean
  lastName: string
  firstName: string
  email: string
  phone: string
  company: string
  vatNumber: string
  deliveryNote: string
  billing: AddressFields
  shipping: AddressFields
  sameAddress: boolean

  /* 04 · summary */
  payment: PaymentMethod
  acceptTerms: boolean
  acceptCancellation: boolean
  /**
   * `build/FIXES.md` item 79 (F8): *Megjegyzés vagy különleges kérés*, always
   * available, at the end of the order and before the order button, 500
   * characters. A non-empty comment is itself a confirmation reason, which is
   * why it is a field of its own rather than a second dietary note.
   */
  comment: string

  /* the rest */
  /** Fix list item 9: on one column the summary is a bottom bar, and this is
   *  whether it is expanded into the full panel. Ignored on two columns. */
  offerOpen: boolean
  zoom: string | null
  callbackOpen: boolean
  /** DR-8 (FIXES item 11): the field stops mirroring step 03 once edited. */
  callbackTouched: boolean
  callbackField: string
  callbackBad: boolean
  callbackDone: CallbackRecord | null
  submitting: boolean
  sent: SentRecord | null
  /** One plain sentence from the server when a submission was refused. */
  submitError: Bilingual | null
  /**
   * §9.6: "Reservation fails → Unchanged; retry." The order was placed and
   * holds its capacity; what failed is the start of the payment. Not a
   * `submitError`, because the submission succeeded and saying otherwise
   * would tell the customer to do the one thing they must not — place it
   * again.
   */
  paymentError: boolean
  /**
   * SimplePay test prompt of 3 November 2026, §1: the server's answer to
   * whether the email entered is on the SimplePay test allowlist — true or
   * false, never the list. False until it has answered.
   */
  simplepayTest: boolean
  /** §6: the SP doc §8 data-transfer notice, ticked. Never pre-ticked. */
  acceptSimplePay: boolean
}

/**
 * The page as it opens. The occasion, the serving window, the profile and the
 * balance are the §6.1–§6.3 defaults and the *Javasolt* of `build/FIXES.md`
 * item 2; the date is set once the context says which business days there
 * are, so nothing here guesses a calendar.
 */
export function initialState(lang: Lang): PageState {
  return {
    lang,
    eventName: '',
    /* Fix list item 4 (Barna, 10 October 2026): the page opens with no
       headcount, so there is no offer and no amount until the customer
       types one. `offerIsReady()` reads the empty field. */
    guestsField: '',
    guestsCapped: false,
    date: '',
    start: '12:00',
    /* Run 19 §5 (DECIDED 20261016, Barna): *where the page pre-selects an
       occasion, it is Állófogadás*. The chip stands for two register rows
       and item B11(a) picks between them by the start time, so the page
       opens on the row that start time earns — `fog_nap` at 12:00 — and
       `CustomerPage`'s own effect keeps it in step until the customer
       answers the meal question for themselves. It was `munka`. */
    occasion: 'fog_nap',
    servingWindow: '2h',
    profile: 'average',
    balance: 'rec',
    mealTouched: false,
    staff: false,
    planteenOnly: true,
    /* Fix list item 6 (Barna, 10 October 2026): lunch defaults to *from
       the Planteen*. The engine's CONFERENCE_DEFAULTS, which the server
       falls back on when no answer is sent, is left as it was; the page
       always sends its answers. */
    conference: { ...CONFERENCE_DEFAULTS, lunch: true },
    coffee: false,
    glutenFreeGuests: 0,
    dietaryNote: '',
    fulfilment: 'delivery',
    postcode: DEFAULT_POSTCODE,
    slotId: '',
    selection: null,
    selectionKey: '',
    touched: { sav: false, sw: false },
    clipped: false,
    clipLimitKey: '',
    quantities: {},
    quantitiesHeld: false,
    removed: null,
    sections: [],
    filter: 'all',
    sub: null,
    fruit: [],
    drinks: {},
    custom: false,
    business: false,
    lastName: '',
    firstName: '',
    email: '',
    phone: '',
    company: '',
    vatNumber: '',
    deliveryNote: '',
    /* DR-22 (`build/FIXES.md` item 25): the step 01 postcode and the address
       postcode are one value, so the address block opens holding it rather
       than empty — which is what vB6 does, by calling its own sync once at
       startup. The town follows the postcode the same way it does on every
       later edit. */
    billing: { ...emptyAddress(), postcode: DEFAULT_POSTCODE, town: 'Budapest' },
    shipping: emptyAddress(),
    sameAddress: true,
    payment: 'card',
    acceptTerms: false,
    acceptCancellation: false,
    comment: '',
    offerOpen: false,
    zoom: null,
    callbackOpen: false,
    callbackTouched: false,
    callbackField: '',
    callbackBad: false,
    callbackDone: null,
    submitting: false,
    sent: null,
    submitError: null,
    paymentError: false,
    simplepayTest: false,
    acceptSimplePay: false,
  }
}

/** §5.2: the headcount the page works with, whatever is in the field. */
export const guestsOf = (state: PageState): number =>
  Math.min(400, Math.max(1, Math.round(Number(state.guestsField) || 1)))

/**
 * §5.10: the address the food goes to. Ticking *same* makes the billing
 * address the delivery address, which is why the shipping block is hidden
 * rather than cleared.
 */
export const shippingAddress = (state: PageState): AddressFields =>
  state.sameAddress ? state.billing : state.shipping
